import React, {Fragment} from 'react';
import {AlignerByScreen} from '@gongxigames/framework/core/fragments/react';
import {AutoplayOptionsPopupData} from '@gongxigames/framework/slot/modules/autoplayOptions/AutoplayOptionsPopup';
import {AutoplayOptionsPopupComponent} from '@gongxigames/framework/slot/modules/autoplayOptions/popup/AutoplayOptionsPopupComponent';
import {AutoplayConfirmationPopupComponent} from '@gongxigames/framework/slot/modules/autoplayOptions/popup//AutoplayConfirmationPopupComponent';
import {Container} from '@inlet/react-pixi';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {Rectangle} from 'pixi.js';
import {SystemPopupPropsTypes} from '@gongxigames/framework/slot/types';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {
    CoreDI,
    ICurrencyFormatter,
    IEventManagerWithAsync,
    ISoundPlayer,
    lazyInject,
    ScreenManager,
    ScreenParameters
} from '@gongxigames/framework/core';

export type AutoplayOptionsPopupViewProps = SystemPopupPropsTypes<AutoplayOptionsPopupData>;

export type AutoplayOptionsPopupViewState = {
    hitArea: Rectangle;
    wagerFormatedValue: string;
    showOptionsPopup: boolean
};

export class AutoplayOptionsPopupView extends React.Component<AutoplayOptionsPopupViewProps> {
    public state: AutoplayOptionsPopupViewState = {
        hitArea: new Rectangle(),
        wagerFormatedValue: '',
        showOptionsPopup: true
    };

    @lazyInject(DI.SoundPlayer)
    private _soundPlayer!: ISoundPlayer;

    @lazyInject(CoreDI.IEventManagerWithAsync)
    private _eventManager!: IEventManagerWithAsync<GlobalEventsMap>;

    @lazyInject(CoreDI.ScreenManager)
    private _screenManager!: ScreenManager;

    @lazyInject(CoreDI.CurrencyFormatter)
    private formatter!: ICurrencyFormatter;

    public componentWillUnmount(): void {
        this.props.data.options.off('onChange', this);
        this._eventManager.off('General.Screen.RESIZED', this);
    }

    public componentDidMount(): void {
        this.props.data.options.onChange(() => this.forceUpdate(), this);
        this._eventManager.on('General.Screen.RESIZED', this.onResize.bind(this), this);

        this.updateHitArea(this._screenManager.getScreenState());
        this.setWager();
    }

    public render(): JSX.Element {
        const {
            roundsOptions,
            turboModeDisabled,
            disabledLimits,
            autoplayShowBetAndWager,
            autoplayShowConfirmationPopup,
            totalBet
        } = this.props.data;
        const {
            singleWin,
            autoplaySpins,
            winLimit,
            lossLimit,
            notValidValues,
            turboMode
        } = this.props.data.options.data;
        const betFormatedValue = this.formatter.formatCents(totalBet ?? 0);

        return (
            <Fragment>
                <Container hitArea={this.state.hitArea} interactive={true} pointertap={
                    autoplayShowConfirmationPopup && !this.state.showOptionsPopup ?
                    this.closeConfirmationPopup.bind(this) :
                    this.close.bind(this)
                }/>
                <AlignerByScreen horizontalAlign={'center'} verticalAlign={'center'} >
                    {this.state.showOptionsPopup
                        ?
                        <AutoplayOptionsPopupComponent
                            singleWin={singleWin}
                            autoplaySpins={autoplaySpins}
                            winLimit={winLimit}
                            lossLimit={lossLimit}
                            notValidValues={notValidValues}
                            turboMode={turboMode}
                            turboModeDisabled={turboModeDisabled}
                            disabledLimits={disabledLimits}
                            roundsOptions={roundsOptions}
                            autoplayShowBetAndWager={autoplayShowBetAndWager}
                            betFormatedValue={betFormatedValue}
                            wagerFormatedValue={this.state.wagerFormatedValue}
                            onClickClose={this.onClickClose.bind(this)}
                            onClickStart={autoplayShowConfirmationPopup ? this.onClickStartConfirmationPopup.bind(this) : this.onClickStart.bind(this)}
                            onChangeLossLimit={this.onChangeLossLimit.bind(this)}
                            onChangeRounds={this.onChangeRounds.bind(this)}
                            onChangeSingleWin={this.onChangeSingleWin.bind(this)}
                            onChangeWinLimit={this.onChangeWinLimit.bind(this)}
                            onToggleTurboMode={this.onToggleTurboMode.bind(this)}
                        />
                        :
                        <AutoplayConfirmationPopupComponent
                            onClickNo={this.onClickNoConfirmationPopup.bind(this)}
                            onClickYes={this.onClickStart.bind(this)}
                            betFormatedValue={betFormatedValue}
                            wagerFormatedValue={this.state.wagerFormatedValue}
                            autoplaySpins={autoplaySpins!}
                        />
                    }
                </AlignerByScreen>
            </Fragment>
        );
    }

    private isToggledValue(newValue: number | null, previousValue: number | null): boolean {
        return (newValue === null && previousValue !== null) || (newValue !== null && previousValue === null);
    }

    private onClickClose(): void {
        this._soundPlayer.play('buttonclick');
        this.close();
    }

    private close(): void {
        this.props.data.closeAutoplayOptionsPopup();
    }

    private onClickStart(): void {
        this._soundPlayer.play('buttonclick');
        this.props.data.startAutoplay();
    }

    private onClickStartConfirmationPopup(): void {
        this._soundPlayer.play('buttonclick');
        this.setState({showOptionsPopup: false});
    }

    private closeConfirmationPopup(): void {
        this.setState({showOptionsPopup: true});
    }

    private onClickNoConfirmationPopup(): void {
        this._soundPlayer.play('buttonclick');
        this.closeConfirmationPopup();
    }

    private onToggleTurboMode(turboMode: boolean | null): void {
        this._soundPlayer.play('buttontoggle');

        this.props.data.setOption('turboMode', turboMode);
    }

    private onChangeSingleWin(singleWin: number | null): void {
        if (this.isToggledValue(singleWin, this.props.data.options.data.singleWin)) {
            this._soundPlayer.play('buttontoggle');
        }

        this.props.data.setOption('singleWin', singleWin);
    }

    private onChangeWinLimit(winLimit: number | null): void {
        if (this.isToggledValue(winLimit, this.props.data.options.data.winLimit)) {
            this._soundPlayer.play('buttontoggle');
        }

        this.props.data.setOption('winLimit', winLimit);
    }

    private onChangeLossLimit(lossLimit: number | null): void {
        if (this.isToggledValue(lossLimit, this.props.data.options.data.lossLimit)) {
            this._soundPlayer.play('buttontoggle');
        }

        this.props.data.setOption('lossLimit', lossLimit);
    }

    private onChangeRounds(autoplaySpins: number | null): void {
        this._soundPlayer.play('buttonclick');

        this.props.data.setOption('autoplaySpins', autoplaySpins);
        this.setWager();
    }

    protected setWager(): void {
        if (!this.props.data.autoplayShowConfirmationPopup) {
            return;
        }

        const autoplaySpins = this.props.data.options.data?.autoplaySpins ?? 0;
        const totalBet = this.props.data?.totalBet ?? 0;

        this.setState({wagerFormatedValue: this.formatter.formatCents(autoplaySpins * totalBet)});
    }

    private onResize(params: ScreenParameters): void {
        this.updateHitArea(params);
    }

    private updateHitArea(params: ScreenParameters): void {
        const {x, y, scale, width, height} = params;
        const paddingX = x / scale;
        const paddingY = y / scale;
        const screenWidth = paddingX * 2 + width;
        const screenHeight = paddingY * 2 + height;
        const hitArea = new Rectangle(-paddingX, -paddingY, screenWidth, screenHeight);

        this.setState({hitArea});
    }
}
